import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { useEffect, useState, useMemo, useCallback, memo, useId, lazy, Suspense } from "react";
import { useQuery, useMutation, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useSectionCategories } from "@/hooks/use-section-categories";
import { crmTaskBadges } from "@/lib/crm-tasks.functions";
import {
  Loader2,
  Plus,
  Search,
  UtensilsCrossed,
  Sparkles,
  CalendarDays,
  AlertTriangle,
  CircleDot,
  Users,
  CheckCircle2,
  TrendingUp,
  Clock,
  MapPin,
  Filter,
  X,
  Inbox,
  ChevronDown,
  ChevronRight,
  Building2,
  Trash2,
  LayoutGrid,
  List as ListIcon,
  Mail,
  Phone,
  CalendarPlus,
  Eye,
  MessageCircle,
  Globe,
  Video,
  Lock as LockIcon,
  CalendarClock,
} from "lucide-react";


import { useAuth } from "@/hooks/use-auth";
import {
  crmListCities,
  crmListClients,
  crmUpsertClient,
  crmGetMyAccess,
  crmDeleteClient,
  crmCreateCity,
  crmSetClientStage,
  crmUpdateClientNotes,
} from "@/lib/crm.functions";
import { getPartnerApplicationCounts } from "@/lib/partner-signups.functions";
import { crmScheduleMeeting } from "@/lib/crm-meetings.functions";
import {
  googleCalendarStart,
  googleCalendarStatus,
  createGoogleMeetForMeeting,
} from "@/lib/google-calendar.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export const Route = createFileRoute("/crm/")({
  validateSearch: (s: Record<string, unknown> & SearchSchemaInput) => ({
    mine: s.mine === "1" || s.mine === 1 || s.mine === true ? 1 : undefined,
  }),
  component: CRMDashboard,
});

const ClientDetailBody = lazy(() =>
  import("@/components/crm/ClientDetailView").then((mod) => ({ default: mod.ClientDetailBody })),
);

const ComposeEmailDialog = lazy(() =>
  import("@/components/crm/ComposeEmailDialog").then((m) => ({ default: m.ComposeEmailDialog })),
);

const SEGMENTS = [
  { value: "eat_drink", label: "Eat & Drink", icon: UtensilsCrossed },
  { value: "do", label: "Do", icon: Sparkles },
  { value: "happenings", label: "Happenings", icon: CalendarDays },
] as const;

type SegmentVal = (typeof SEGMENTS)[number]["value"];

const SEGMENT_TO_SECTION: Record<SegmentVal, "eat" | "do" | "happening"> = {
  eat_drink: "eat",
  do: "do",
  happenings: "happening",
};

function alertTint(endsAt: string | null | undefined) {
  if (!endsAt) return null;
  const end = new Date(endsAt).getTime();
  const now = Date.now();
  const days = (end - now) / 86_400_000;
  if (days < 0) return "red";
  if (days <= 30) return "yellow";
  return null;
}

function CRMDashboard() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const search$ = Route.useSearch();
  const listCities = useServerFn(crmListCities);
  const listClients = useServerFn(crmListClients);
  const getTaskBadges = useServerFn(crmTaskBadges);
  const getAccess = useServerFn(crmGetMyAccess);
  const deleteClient = useServerFn(crmDeleteClient);

  const [cityId, setCityId] = useState<string | null>(null);
  const [segment, setSegment] = useState<SegmentVal | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const [onlyMine, setOnlyMine] = useState<boolean>(search$.mine === 1);
  const [openNew, setOpenNew] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [statFilter, setStatFilter] = useState<"all" | "active" | "prospect" | "expiring">("all");
  const [viewMode, setViewMode] = useState<"list" | "kanban">("kanban");
  const [detailClientId, setDetailClientId] = useState<string | null>(null);
  const openClientDetail = useCallback((id: string) => setDetailClientId(id), []);
  const isMyListView = search$.mine === 1;

  // Sync onlyMine with ?mine=1 from sidebar "My List" link.
  useEffect(() => {
    const nextOnlyMine = search$.mine === 1;
    setOnlyMine(nextOnlyMine);
    if (nextOnlyMine) setStatFilter("all");
  }, [search$.mine]);

  const accessQuery = useQuery({
    queryKey: ["crm", "me"],
    queryFn: () => getAccess(),
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const countsFn = useServerFn(getPartnerApplicationCounts);
  const applicationCountsQuery = useQuery({
    queryKey: ["admin", "partner-application-counts"],
    queryFn: () => countsFn(),
    enabled: !!user && !!accessQuery.data?.has_crm_access,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  const waitingApplications = applicationCountsQuery.data?.submitted ?? 0;

  const citiesQuery = useQuery({
    queryKey: ["crm", "cities"],
    queryFn: () => listCities(),
    enabled: !!user && !!accessQuery.data?.has_crm_access,
    staleTime: 15 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const taskBadgesQuery = useQuery({
    queryKey: ["crm", "task-badges"],
    queryFn: () => getTaskBadges(),
    enabled: !!user && !!accessQuery.data?.has_crm_access,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const taskBadges = taskBadgesQuery.data?.badges ?? {};

  const clientsQuery = useQuery({
    queryKey: ["crm", "clients", { cityId, segment, search: debouncedSearch, onlyMine }],
    queryFn: () =>
      listClients({
        data: {
          city_id: cityId,
          segment: segment ?? undefined,
          search: debouncedSearch || undefined,
          only_mine: onlyMine,
        },
      }),
    enabled: !!user && !!accessQuery.data?.has_crm_access,
    staleTime: 2 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("crm-clients-list-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_clients" }, () => {
        queryClient.invalidateQueries({ queryKey: ["crm", "clients"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_deals" }, () => {
        queryClient.invalidateQueries({ queryKey: ["crm", "clients"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_contracts" }, () => {
        queryClient.invalidateQueries({ queryKey: ["crm", "clients"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteClient({ data: { id } }),
    onSuccess: () => {
      toast.success("Client deleted");
      setDeleteTarget(null);
      clientsQuery.refetch();
    },
    onError: (e: any) => {
      toast.error(e?.message ?? "Failed to delete client");
    },
  });

  const cities = citiesQuery.data?.cities ?? [];
  const clients = clientsQuery.data?.clients ?? [];
  const contracts = clientsQuery.data?.contracts ?? {};
  const canDelete = accessQuery.data?.is_admin === true;

  const stats = useMemo(() => {
    let active = 0, prospects = 0, expiring = 0;
    for (const c of clients as any[]) {
      if (c.status === "active") active++;
      if (c.status === "prospect") prospects++;
      const t = alertTint(contracts[c.id]?.ends_at);
      if (t === "yellow" || t === "red") expiring++;
    }
    return { total: clients.length, active, prospects, expiring };
  }, [clients, contracts]);

  const filteredClients = useMemo(() => {
    if (statFilter === "all") return clients;
    return (clients as any[]).filter((c) => {
      if (statFilter === "active") return c.status === "active";
      if (statFilter === "prospect") return c.status === "prospect";
      if (statFilter === "expiring") {
        const t = alertTint(contracts[c.id]?.ends_at);
        return t === "yellow" || t === "red";
      }
      return true;
    });
  }, [clients, contracts, statFilter]);

  const STAT_LABELS: Record<"all" | "active" | "prospect" | "expiring", string> = {
    all: "All",
    active: "Active",
    prospect: "Prospects",
    expiring: "Expiring",
  };

  const activeFilters = [
    cityId && { key: "city", label: cities.find((c: any) => c.id === cityId)?.name ?? "City", clear: () => setCityId(null) },
    segment && { key: "seg", label: SEGMENTS.find((s) => s.value === segment)?.label ?? segment, clear: () => setSegment(null) },
    search && { key: "search", label: `"${search}"`, clear: () => setSearch("") },
    onlyMine && !isMyListView && { key: "mine", label: "My Pipeline", clear: () => setOnlyMine(false) },
    statFilter !== "all" && { key: "stat", label: STAT_LABELS[statFilter], clear: () => setStatFilter("all") },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  if ((loading || accessQuery.isLoading) && !accessQuery.data) {
    return <CRMPageShellSkeleton />;
  }
  if (!accessQuery.data) {
    return (
      <div className="grid place-items-center py-24 text-center">
        <div>
          <p>Sign in required.</p>
          <Button className="mt-4" onClick={() => navigate({ to: "/login" })}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
        <div className="flex items-center justify-end gap-2">
            <Link
              to="/crm/applications"
              className="relative inline-flex min-w-0 items-center justify-center gap-1.5 rounded-lg border border-[#e7eaf0] bg-white px-3 py-2 text-sm font-medium text-[#1a2540] shadow-sm hover:bg-[#f4f6fb] active:scale-[0.98] transition"
            >
              <Inbox className="h-4 w-4 shrink-0" />
              <span className="truncate">Apps</span>
              {waitingApplications > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white shadow-sm">
                  {waitingApplications > 99 ? "99+" : waitingApplications}
                </span>
              )}
            </Link>
            <Button
              onClick={() => setOpenNew(true)}
              className="h-auto justify-center py-2 shadow-sm"
            >
              <Plus className="mr-1.5 h-4 w-4 shrink-0" />
              <span className="truncate">New</span>
            </Button>
        </div>


        {!isMyListView && (
          <section className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
            {([
              { key: "all", label: "Clients", value: stats.total, icon: Users, tint: "#0d6efd" },
              { key: "active", label: "Active", value: stats.active, icon: CheckCircle2, tint: "#198754" },
              { key: "prospect", label: "Prospects", value: stats.prospects, icon: TrendingUp, tint: "#0ea5e9" },
              { key: "expiring", label: "Expiring", value: stats.expiring, icon: Clock, tint: "#f59e0b" },
            ] as const).map((s) => {
              const Icon = s.icon;
              const isActive = statFilter === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStatFilter(statFilter === s.key && s.key !== "all" ? "all" : (s.key as typeof statFilter))}
                  className={`flex items-center gap-3 rounded-xl border bg-white px-3 py-2.5 text-left transition-colors ${
                    isActive ? "ring-2 ring-offset-1" : "border-[#e7eaf0] hover:bg-[#f8f9fb]"
                  }`}
                  style={isActive ? ({ ["--tw-ring-color" as any]: s.tint, borderColor: "transparent" } as any) : undefined}
                >
                  <span
                    className="grid size-8 place-items-center rounded-md shrink-0"
                    style={{ background: `${s.tint}1a`, color: s.tint }}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[10px] font-semibold uppercase tracking-wide text-[#6c757d]">{s.label}</span>
                    <span className="block text-xl font-bold tabular-nums text-[#212529] leading-tight">{s.value}</span>
                  </span>
                </button>
              );
            })}
          </section>
        )}

{!isMyListView && (
        <section className="relative rounded-xl border border-[#e7eaf0] bg-white shadow-sm overflow-hidden">
          {/* Header — collapsible on mobile */}
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className="relative w-full flex items-center gap-2 border-b border-[#eef0f4] px-3 py-2.5 sm:px-4 sm:cursor-default sm:pointer-events-none"
            aria-expanded={filtersOpen}
            aria-controls="crm-filter-chips"
          >
            <span className="flex items-center gap-2 px-2 py-1 bg-[#e7f1ff] text-[#0d6efd] rounded-md border border-[#cfe2ff]">
              <Filter className="h-4 w-4" />
              <span className="text-[11px] font-bold uppercase tracking-wider">Filters</span>
              {activeFilters.length > 0 && (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#0d6efd] px-1 text-[10px] font-bold text-white">
                  {activeFilters.length}
                </span>
              )}
            </span>
            <span className="ml-auto flex items-center gap-2">
              {activeFilters.length > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCityId(null);
                    setSegment(null);
                    setSearch("");
                    if (!isMyListView) setOnlyMine(false);
                  }}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-[#0d6efd] hover:bg-[#e7f1ff] sm:pointer-events-auto pointer-events-auto"
                >
                  <X className="h-3 w-3" /> Clear
                </span>
              )}
              <ChevronDown
                className={`h-4 w-4 text-[#6c757d] transition-transform sm:hidden ${
                  filtersOpen ? "rotate-180" : ""
                }`}
              />
            </span>
          </button>

          <div className="relative p-3 sm:p-3.5 space-y-3">
            {/* Search + my-pipeline — always visible */}
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
              <div className="group relative min-w-0">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#adb5bd] transition-colors group-focus-within:text-[#0d6efd]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, company or Business ID…"
                  className="h-9 pl-9 pr-3 bg-[#f8f9fb] border-[#e7eaf0] focus-visible:bg-white focus-visible:border-[#0d6efd] focus-visible:ring-1 focus-visible:ring-[#0d6efd]/15 rounded-lg text-sm transition-all"
                />
              </div>
              {!isMyListView && (accessQuery.data?.scope === "all" || accessQuery.data?.scope === "cities") && (
                <button
                  onClick={() => setOnlyMine((v) => !v)}
                  aria-pressed={onlyMine}
                  aria-label="My Pipeline"
                  className={`shrink-0 inline-flex items-center justify-center gap-1.5 rounded-lg border h-9 text-xs font-semibold transition-all px-3 sm:px-3.5 ${
                    onlyMine
                      ? "border-transparent bg-gradient-to-br from-[#0d6efd] to-[#0a58ca] text-white shadow-sm shadow-[#0d6efd]/20"
                      : "border-[#e7eaf0] bg-white text-[#495057] hover:border-[#0d6efd]/40 hover:bg-[#f8f9fa] hover:text-[#0d6efd]"
                  }`}
                >
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">My Pipeline</span>
                  <span className="sm:hidden">Mine</span>
                </button>
              )}
            </div>

            {/* Chip filters — collapsible on mobile, always visible on desktop */}
            <div
              id="crm-filter-chips"
              className={`${filtersOpen ? "block" : "hidden"} sm:block space-y-3`}
            >
              {/* Cities */}
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-1.5 min-w-[64px] pt-1 text-[#6c757d] shrink-0">
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-semibold uppercase tracking-tight">City</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <FilterChip active={cityId === null} onClick={() => setCityId(null)}>
                    All Cities
                  </FilterChip>
                  {cities.map((c: any) => (
                    <FilterChip key={c.id} active={cityId === c.id} onClick={() => setCityId(c.id)}>
                      {c.name}
                    </FilterChip>
                  ))}
                </div>
              </div>

              {/* Segments */}
              <div className="flex items-start gap-3">
                <div className="flex items-center gap-1.5 min-w-[64px] pt-1 text-[#6c757d] shrink-0">
                  <Filter className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-semibold uppercase tracking-tight">Segment</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <FilterChip active={segment === null} onClick={() => setSegment(null)}>
                    All
                  </FilterChip>
                  {SEGMENTS.map((s) => {
                    const Icon = s.icon;
                    return (
                      <FilterChip key={s.value} active={segment === s.value} onClick={() => setSegment(s.value)}>
                        <Icon className="mr-1 h-3 w-3" />
                        {s.label}
                      </FilterChip>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Active chips summary — always visible */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-t border-dashed border-[#eef0f4] pt-2.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#6c757d] self-center mr-1">Active:</span>
                {activeFilters.map((f) => (
                  <button
                    key={f.key}
                    onClick={f.clear}
                    className="inline-flex items-center gap-1 rounded-full bg-[#e7f1ff] border border-[#cfe2ff] px-2.5 py-1 text-[11px] font-semibold text-[#0a58ca] hover:bg-[#dbe9ff] transition-all"
                  >
                    {f.label}
                    <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
        )}




        {/* Body card */}
        <section className="relative overflow-hidden rounded-2xl border border-white/60 bg-gradient-to-br from-[#fbfcff] via-[#f5f7ff] to-[#f8f5ff] shadow-[0_10px_40px_-15px_rgba(13,110,253,0.18)] backdrop-blur-sm">
          <div className="flex items-center justify-between gap-2 border-b border-[#eef0f4]/70 bg-gradient-to-r from-[#f8f9fb]/60 via-white/40 to-[#f5f7ff]/60 px-4 py-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="grid size-7 place-items-center rounded-lg bg-gradient-to-br from-[#0d6efd] to-[#6610f2] text-white shadow-sm">
                <Users className="h-3.5 w-3.5" />
              </span>
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#212529]">
                {isMyListView ? "My List" : "Client Pipeline"}
              </h2>
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e7f1ff] px-1.5 text-[10px] font-bold text-[#0a58ca] tabular-nums">
                {filteredClients.length}{statFilter !== "all" && <span className="text-[#6c757d]"> / {clients.length}</span>}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="inline-flex rounded-lg border border-[#e7eaf0] bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  aria-pressed={viewMode === "list"}
                  aria-label="List view"
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${
                    viewMode === "list"
                      ? "bg-[#0d6efd] text-white shadow-sm"
                      : "text-[#495057] hover:bg-[#f1f3f5]"
                  }`}
                >
                  <ListIcon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">List</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("kanban")}
                  aria-pressed={viewMode === "kanban"}
                  aria-label="Pipeline view"
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${
                    viewMode === "kanban"
                      ? "bg-[#0d6efd] text-white shadow-sm"
                      : "text-[#495057] hover:bg-[#f1f3f5]"
                  }`}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Pipeline</span>
                </button>
              </div>
              <button
                onClick={() => setOpenNew(true)}
                className="hidden sm:inline-flex items-center gap-1 rounded-lg bg-[#0d6efd] px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-[#0a58ca] transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
          </div>



          {clientsQuery.isPending && !clientsQuery.data ? (
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-40 rounded-xl border bg-[#f8f9fb] animate-pulse" style={{ borderColor: "#eef0f4" }} />
              ))}
            </div>
          ) : clientsQuery.isError ? (
            <div className="grid place-items-center py-20 px-4 text-center">
              <div className="grid h-14 w-14 place-items-center rounded-full bg-amber-50 text-amber-600 mb-3">
                <Inbox className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-[#495057]">
                Can't load this view
              </p>
              <p className="mt-1 text-xs text-[#6c757d] max-w-xs">
                {(clientsQuery.error as Error)?.message ??
                  "You don't have permission to view these clients."}
              </p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="grid place-items-center py-20 px-4 text-center">
              <div className="grid h-14 w-14 place-items-center rounded-full bg-[#f4f6f9] text-[#adb5bd] mb-3">
                <Inbox className="h-6 w-6" />
              </div>
              <p className="text-sm font-medium text-[#495057]">
                {clients.length === 0
                  ? (isMyListView ? "No clients in my list yet" : "No clients yet")
                  : `No ${STAT_LABELS[statFilter].toLowerCase()} clients`}
              </p>
              <p className="mt-1 text-xs text-[#6c757d]">
                {clients.length === 0
                  ? (isMyListView ? "Clients you add will appear here." : "Tap \"New Client\" to add your first one.")
                  : "Try a different filter."}
              </p>
              {clients.length === 0 ? (
                <Button size="sm" className="mt-4" onClick={() => setOpenNew(true)}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> New Client
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="mt-4" onClick={() => setStatFilter("all")}>
                  Show all
                </Button>
              )}
            </div>
          ) : viewMode === "kanban" ? (
            <KanbanBoard
              clients={filteredClients}
              cities={cities}
              contracts={contracts}
              me={accessQuery.data}
              userId={user?.id}
              onStageChange={() => clientsQuery.refetch()}
              onOpen={openClientDetail}
              canDelete={canDelete}
              onDelete={(id, name) => setDeleteTarget({ id, name })}
            />
          ) : (
            <ul className="divide-y divide-[#eef0f4]">
              {filteredClients.map((c: any) => {
                const tint = alertTint(contracts[c.id]?.ends_at);
                const taskBadge = taskBadges[c.id];
                const taskTone = taskBadge?.overdue ? "overdue" : taskBadge?.open ? "open" : null;
                const cityName = cities.find((ct: any) => ct.id === c.city_id)?.name;
                const seg = SEGMENTS.find((s) => s.value === c.segment);
                const SegIcon = seg?.icon ?? CircleDot;
                const segColor =
                  c.segment === "eat_drink" ? "#f97316" :
                  c.segment === "happenings" ? "#a855f7" :
                  c.segment === "do" ? "#0ea5e9" : "#0d6efd";
                const displayName = c.company_name?.trim() || "—";
                const initials = String(displayName || "?")
                  .split(/\s+/).filter(Boolean).slice(0, 2)
                  .map((w: string) => w[0]?.toUpperCase()).join("") || "?";
                return (
                  <li
                    key={c.id}
                    className={`group relative ${
                      taskTone === "overdue"
                        ? "bg-rose-50/40"
                        : taskTone === "open"
                          ? "bg-sky-50/30"
                          : ""
                    }`}
                  >
                    {/* Segment accent bar */}
                    <span
                      aria-hidden
                      className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full opacity-70 group-hover:opacity-100 transition-opacity"
                      style={{ background: `linear-gradient(180deg, ${segColor}, ${segColor}99)` }}
                    />
                    <button
                      type="button"
                      onClick={() => openClientDetail(c.id)}
                      className="relative grid w-full text-left grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 pl-4 pr-3 py-3 sm:py-3.5 hover:bg-[#f8f9fb] active:bg-[#f1f3f5] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd]"
                    >
                      {/* Avatar */}
                      <div
                        className="relative grid h-11 w-11 shrink-0 place-items-center rounded-xl text-sm font-bold text-white shadow-sm"
                        style={{ background: `linear-gradient(135deg, ${segColor}, ${segColor}cc)` }}
                      >
                        {initials}
                        <span
                          className="absolute -bottom-1 -right-1 grid size-4 place-items-center rounded-full bg-white shadow-sm ring-1 ring-[#eef0f4]"
                          style={{ color: segColor }}
                        >
                          <SegIcon className="h-2.5 w-2.5" />
                        </span>
                      </div>

                      {/* Body */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <h3 className="truncate text-sm font-semibold text-[#1a2540]">
                            {displayName}
                          </h3>
                          {taskTone && (
                            <span
                              title={
                                taskBadge?.next_due_at
                                  ? `Next task: ${new Date(taskBadge.next_due_at).toLocaleString()}`
                                  : undefined
                              }
                              className={`shrink-0 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                                taskTone === "overdue"
                                  ? "bg-rose-100 text-rose-700"
                                  : "bg-sky-50 text-sky-700"
                              }`}
                            >
                              <CalendarClock className="h-2.5 w-2.5" />
                              {taskTone === "overdue"
                                ? `${taskBadge?.overdue} overdue`
                                : `${taskBadge?.open} task${(taskBadge?.open ?? 0) > 1 ? "s" : ""}`}
                            </span>
                          )}
                          {tint && (
                            <span
                              className={`shrink-0 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                                tint === "red"
                                  ? "bg-rose-50 text-rose-600"
                                  : "bg-amber-50 text-amber-700"
                              }`}
                            >
                              <AlertTriangle className="h-2.5 w-2.5" />
                              {tint === "red" ? "Expired" : "Soon"}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[#6c757d]">
                          {cityName && (
                            <span className="inline-flex items-center gap-1 min-w-0">
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span className="truncate">{cityName}</span>
                            </span>
                          )}
                          <span
                            className="shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                            style={{ background: `${segColor}14`, color: segColor }}
                          >
                            {seg?.label ?? c.segment}
                          </span>
                          {c.subcategory && (
                            <span className="hidden sm:inline truncate">· {c.subcategory}</span>
                          )}
                          {c.business_id && (
                            <span className="hidden md:inline-flex items-center gap-1 truncate">
                              <Building2 className="h-3 w-3" /> {c.business_id}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right side */}
                      <div className="flex shrink-0 items-center gap-2">
                        <StageSelect
                          clientId={c.id}
                          value={stageFor(c, contracts[c.id])}
                          canEdit={clientCanEdit(c, accessQuery.data, user?.id)}
                          onChanged={() => clientsQuery.refetch()}
                        />
                        {canDelete && <span className="w-8" aria-hidden />}
                        <ChevronRight className="h-4 w-4 text-[#adb5bd] group-hover:text-[#0d6efd] group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDeleteTarget({ id: c.id, name: displayName });
                        }}
                        aria-label={`Delete ${displayName}`}
                        className="absolute right-10 top-1/2 -translate-y-1/2 grid h-8 w-8 place-items-center rounded-lg text-[#adb5bd] hover:bg-rose-50 hover:text-rose-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>


        <NewClientDialog
          open={openNew}
          onClose={() => setOpenNew(false)}
          cities={cities}
          onCreated={() => clientsQuery.refetch()}
          onCityAdded={() => citiesQuery.refetch()}
        />

        <AlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => {
            if (!o && !deleteMutation.isPending) setDeleteTarget(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this client?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes{" "}
                <span className="font-semibold text-foreground">
                  {deleteTarget?.name}
                </span>{" "}
                and all related deals, contracts, and contact history. This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={deleteMutation.isPending}
                onClick={(e) => {
                  e.preventDefault();
                  if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
                }}
                className="bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600"
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…
                  </>
                ) : (
                  "Delete Client"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={!!detailClientId} onOpenChange={(o) => { if (!o) setDetailClientId(null); }}>
          <DialogContent className="max-w-5xl w-[96vw] p-0 gap-0 max-h-[92vh] overflow-x-hidden overflow-y-auto">
            <DialogHeader className="sr-only">
              <DialogTitle>Client details</DialogTitle>
            </DialogHeader>
            {detailClientId && (
              <Suspense fallback={<div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}>
                <ClientDetailBody
                  clientId={detailClientId}
                  onClose={() => setDetailClientId(null)}
                />
              </Suspense>
            )}
          </DialogContent>
        </Dialog>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  tone: "indigo" | "emerald" | "sky" | "amber";
  active?: boolean;
  onClick?: () => void;
}) {
  const tones: Record<string, string> = {
    indigo: "from-indigo-500/10 to-indigo-500/0 text-indigo-600",
    emerald: "from-emerald-500/10 to-emerald-500/0 text-emerald-600",
    sky: "from-sky-500/10 to-sky-500/0 text-sky-600",
    amber: "from-amber-500/15 to-amber-500/0 text-amber-600",
  };
  const ringTone: Record<string, string> = {
    indigo: "ring-indigo-500",
    emerald: "ring-emerald-500",
    sky: "ring-sky-500",
    amber: "ring-amber-500",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!active}
      className={`relative overflow-hidden rounded-xl border bg-white p-4 shadow-sm text-left transition-all hover:shadow-md active:scale-[0.98] ${
        active ? `border-transparent ring-2 ${ringTone[tone]}` : "border-[#e7eaf0]"
      }`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${tones[tone]} pointer-events-none`} />
      <div className="relative grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wider text-[#6c757d]">{label}</p>
          <p className="mt-1 text-2xl font-bold text-[#1a2540] tabular-nums">{value}</p>
        </div>
        <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/80 ${tones[tone].split(" ").pop()}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </button>
  );
}

function CRMPageShellSkeleton() {
  return (
    <div className="space-y-4">
      <div className="space-y-5">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <div className="min-w-0 space-y-2">
            <div className="h-3 w-32 rounded bg-[#e7eaf0]" />
            <div className="h-7 w-56 max-w-full rounded bg-[#dfe5ef]" />
            <div className="h-4 w-80 max-w-full rounded bg-[#e7eaf0]" />
          </div>
          <div className="h-9 w-28 rounded-md bg-[#dfe5ef]" />
        </div>
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-[90px] rounded-xl border border-[#e7eaf0] bg-white p-4 shadow-sm">
              <div className="h-3 w-24 rounded bg-[#e7eaf0]" />
              <div className="mt-3 h-7 w-10 rounded bg-[#dfe5ef]" />
            </div>
          ))}
        </section>
        <section className="rounded-2xl border border-[#e7eaf0] bg-white p-4 shadow-sm">
          <div className="h-10 rounded-xl bg-[#f1f4f8]" />
          <div className="mt-4 flex flex-wrap gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-8 w-24 rounded-full bg-[#eef2f7]" />
            ))}
          </div>
        </section>
        <section className="overflow-hidden rounded-2xl border border-white/60 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-[#eef0f4] px-4 py-3">
            <div className="h-5 w-36 rounded bg-[#e7eaf0]" />
            <div className="h-7 w-32 rounded-lg bg-[#e7eaf0]" />
          </div>
          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-56 rounded-xl border border-[#eef0f4] bg-[#f8f9fb]" />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}


function FilterChip({
  active,
  onClick,
  children,
  block,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  block?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
        block ? "w-full sm:w-auto" : ""
      } ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-muted active:bg-muted/70"
      }`}
    >
      {children}
    </button>
  );
}


const PIPELINE_STAGES = [
  { key: "prospekti", label: "Prospect", color: "#94a3b8", bg: "#f1f5f9" },
  { key: "kontaktoitu", label: "Contacted", color: "#0ea5e9", bg: "#e0f2fe" },
  { key: "tapaaminen", label: "Meeting", color: "#a855f7", bg: "#f3e8ff" },
  { key: "tarjous", label: "Proposal", color: "#f59e0b", bg: "#fef3c7" },
  { key: "kauppa", label: "Deal", color: "#10b981", bg: "#d1fae5" },
] as const;

function stageFor(c: any, contract: any): typeof PIPELINE_STAGES[number]["key"] {
  if (c.pipeline_stage) return c.pipeline_stage;
  if (c.status === "active" && contract) return "kauppa";
  if (contract) return "tarjous";
  if (c.status === "active") return "tapaaminen";
  return "prospekti";
}

type MeAccess = {
  is_admin?: boolean;
  is_super_admin?: boolean;
  can_edit_others?: boolean;
  edit_grant_rep_ids?: string[];
} | undefined | null;

function clientCanEdit(c: any, me: MeAccess, userId: string | undefined): boolean {
  if (!me || !userId) return false;
  if (me.is_admin || me.is_super_admin) return true;
  if (c.assigned_rep === userId || c.created_by === userId) return true;
  if (me.can_edit_others) return true;
  const grants = me.edit_grant_rep_ids ?? [];
  if (c.assigned_rep && grants.includes(c.assigned_rep)) return true;
  if (c.created_by && grants.includes(c.created_by)) return true;
  return false;
}

function KanbanBoard({
  clients,
  cities,
  contracts,
  me,
  userId,
  onStageChange,
  onOpen,
  canDelete,
  onDelete,
}: {
  clients: any[];
  cities: any[];
  contracts: Record<string, any>;
  me: MeAccess;
  userId: string | undefined;
  onStageChange?: () => void;
  onOpen: (id: string) => void;
  canDelete?: boolean;
  onDelete?: (id: string, name: string) => void;
}) {
  const setStage = useServerFn(crmSetClientStage);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const [pendingStage, setPendingStage] = useState<Record<string, string>>({});
  const [quickViewId, setQuickViewId] = useState<string | null>(null);

  // Build a city-name lookup once so cards don't re-scan the cities array.
  const cityNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const ct of cities) m.set(ct.id, ct.name);
    return m;
  }, [cities]);

  const stageOf = useCallback(
    (c: any) => (pendingStage[c.id] as any) ?? stageFor(c, contracts[c.id]),
    [pendingStage, contracts],
  );

  const grouped = useMemo(
    () =>
      PIPELINE_STAGES.map((s) => ({
        ...s,
        items: clients.filter((c) => stageOf(c) === s.key),
      })),
    [clients, stageOf],
  );

  const handleDrop = useCallback(
    async (stageKey: typeof PIPELINE_STAGES[number]["key"]) => {
      const id = dragId;
      setDragId(null);
      setOverKey(null);
      if (!id) return;
      const current = clients.find((c) => c.id === id);
      if (!current) return;
      if (stageOf(current) === stageKey) return;
      if (!clientCanEdit(current, me, userId)) {
        toast.error("Pipeline move blocked", {
          description:
            "Read-only: you don't have edit access for this client. Ask a super admin to grant edit access.",
        });
        return;
      }
      setPendingStage((p) => ({ ...p, [id]: stageKey }));
      const rollback = () =>
        setPendingStage((p) => {
          const { [id]: _drop, ...rest } = p;
          return rest;
        });
      const explainForbidden = (msg?: string) => {
        if (!msg) return "Failed to move client.";
        if (/forbidden|don't have edit|permission|not allowed/i.test(msg)) {
          return "You can't move this client. You only have edit access to clients assigned to you — ask a super admin to grant edit access for this rep's clients.";
        }
        return msg;
      };
      try {
        const res = await setStage({ data: { id, stage: stageKey } });
        if (!res?.ok) {
          toast.error("Pipeline move blocked", { description: explainForbidden(res?.error) });
          rollback();
          return;
        }
        toast.success(`Moved to ${PIPELINE_STAGES.find((s) => s.key === stageKey)?.label}`);
        onStageChange?.();
      } catch (e: any) {
        toast.error("Pipeline move blocked", { description: explainForbidden(e?.message) });
        rollback();
      }
    },
    [dragId, clients, stageOf, setStage, onStageChange],
  );

  const handleDragStart = useCallback((id: string) => setDragId(id), []);
  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setOverKey(null);
  }, []);
  const handleOpenQuickView = useCallback((id: string) => setQuickViewId(id), []);

  const handleMoveStage = useCallback(
    async (id: string, next: typeof PIPELINE_STAGES[number]["key"]) => {
      const current = clients.find((c) => c.id === id);
      if (!current || stageOf(current) === next) return;
      setPendingStage((p) => ({ ...p, [id]: next }));
      const rollback = () =>
        setPendingStage((p) => {
          const { [id]: _drop, ...rest } = p;
          return rest;
        });
      try {
        const res = await setStage({ data: { id, stage: next } });
        if (!res?.ok) {
          toast.error(res?.error ?? "Failed to move client");
          rollback();
          return;
        }
        toast.success(`Moved to ${PIPELINE_STAGES.find((s) => s.key === next)?.label}`);
        onStageChange?.();
      } catch (e: any) {
        toast.error(e?.message ?? "Failed to move client");
        rollback();
      }
    },
    [clients, stageOf, setStage, onStageChange],
  );

  // Mobile: show one stage at a time via tab pills.
  const [mobileStage, setMobileStage] =
    useState<typeof PIPELINE_STAGES[number]["key"]>("prospekti");
  const mobileIdx = PIPELINE_STAGES.findIndex((s) => s.key === mobileStage);

  // Count map keyed by stage, derived from current `grouped`.
  const stageCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const g of grouped) m[g.key] = g.items.length;
    return m;
  }, [grouped]);

  // On mobile, auto-jump to the first non-empty stage if the current one is empty.
  useEffect(() => {
    if ((stageCounts[mobileStage] ?? 0) > 0) return;
    const firstNonEmpty = PIPELINE_STAGES.find((s) => (stageCounts[s.key] ?? 0) > 0);
    if (firstNonEmpty && firstNonEmpty.key !== mobileStage) {
      setMobileStage(firstNonEmpty.key);
    }
  }, [stageCounts, mobileStage]);

  const goMobile = (delta: number) => {
    // Skip empty stages when stepping with Prev / Next.
    const dir = delta > 0 ? 1 : -1;
    let i = mobileIdx + dir;
    while (i >= 0 && i < PIPELINE_STAGES.length) {
      if ((stageCounts[PIPELINE_STAGES[i].key] ?? 0) > 0) {
        setMobileStage(PIPELINE_STAGES[i].key);
        return;
      }
      i += dir;
    }
  };

  const hasPrevMobile = PIPELINE_STAGES.slice(0, mobileIdx).some(
    (s) => (stageCounts[s.key] ?? 0) > 0,
  );
  const hasNextMobile = PIPELINE_STAGES.slice(mobileIdx + 1).some(
    (s) => (stageCounts[s.key] ?? 0) > 0,
  );


  const quickViewClient = quickViewId ? clients.find((c) => c.id === quickViewId) : null;

  return (
    <div className="px-3 py-3 sm:px-4 sm:py-4">
      {/* Mobile stage tabs */}
      <div className="md:hidden mb-3">
        <div className="rounded-2xl border border-white/50 bg-white/60 p-3 shadow-[0_4px_20px_-8px_rgba(13,110,253,0.12)] backdrop-blur-md">
          <div data-testid="mobile-stage-chips" className="grid grid-cols-2 gap-2">
            {grouped.map((s) => {
              const active = s.key === mobileStage;
              const empty = s.items.length === 0;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setMobileStage(s.key)}
                  aria-pressed={active}
                  aria-disabled={empty}
                  disabled={empty && !active}
                  title={empty ? "No clients in this stage" : undefined}
                  className={`inline-flex w-full min-h-11 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] ${
                    active
                      ? "shadow-[0_2px_8px_-2px_rgba(0,0,0,0.08)]"
                      : empty
                        ? "bg-white/40 text-[#adb5bd] border-dashed border-[#e7eaf0]/60 cursor-not-allowed opacity-60"
                        : "bg-white/70 text-[#6c757d] border-[#e7eaf0]/60 hover:border-[#0d6efd]/30 hover:bg-white/90"
                  }`}
                  style={
                    active
                      ? { background: `linear-gradient(135deg, ${s.bg}, white)`, color: s.color, borderColor: `${s.color}40` }
                      : undefined
                  }
                >
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: empty && !active ? "#ced4da" : s.color }}
                  />
                  {s.label}
                  <span
                    className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold tabular-nums ${
                      active ? "bg-white/80" : "bg-[#f4f6fa]/70"
                    }`}
                    style={active ? { color: s.color } : undefined}
                  >
                    {s.items.length}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#6c757d]">
            <button
              type="button"
              onClick={() => goMobile(-1)}
              disabled={!hasPrevMobile}
              className="inline-flex items-center gap-1 rounded-lg border border-[#e7eaf0]/60 bg-white/70 px-2.5 py-1.5 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd]"
              aria-label="Previous stage with clients"
            >
              ← Prev
            </button>
            <span className="text-[11px] font-bold">
              Stage {mobileIdx + 1} / {PIPELINE_STAGES.length}
            </span>
            <button
              type="button"
              onClick={() => goMobile(1)}
              disabled={!hasNextMobile}
              className="inline-flex items-center gap-1 rounded-lg border border-[#e7eaf0]/60 bg-white/70 px-2.5 py-1.5 disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd]"
              aria-label="Next stage with clients"
            >
              Next →
            </button>
          </div>

        </div>
      </div>

      <div className="md:overflow-x-auto md:-mx-1 md:px-1 lg:overflow-visible lg:mx-0 lg:px-0">
        <div className="flex flex-col md:flex-row md:gap-2.5 md:min-w-[1080px] lg:grid lg:grid-cols-5 lg:gap-3 lg:min-w-0">


          {grouped.map((col) => {
            const isMobileActive = col.key === mobileStage;
            return (
              <section
                key={col.key}
                aria-label={`${col.label} stage, ${col.items.length} clients`}
                onDragOver={(e) => {
                  if (!dragId) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (overKey !== col.key) setOverKey(col.key);
                }}
                onDragLeave={() => setOverKey((k) => (k === col.key ? null : k))}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(col.key);
                }}
                className={`${isMobileActive ? "flex" : "hidden"} md:flex w-full md:w-[220px] md:flex-1 md:basis-0 md:shrink-0 lg:w-auto lg:basis-auto flex-col rounded-xl border bg-[#f8f9fb] transition-colors ${
                  overKey === col.key
                    ? "border-[#0d6efd] ring-2 ring-[#0d6efd]/20 bg-[#e7f1ff]"
                    : "border-[#e7eaf0]"
                }`}
              >
                <div
                  className="hidden md:flex items-center justify-between gap-2 rounded-t-xl border-b border-[#eef0f4] px-3 py-2"
                  style={{ background: col.bg }}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      aria-hidden
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ background: col.color }}
                    />
                    <h3 className="truncate text-[11px] font-bold uppercase tracking-wider text-[#1a2540]">
                      {col.label}
                    </h3>
                  </div>
                  <span
                    className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/80 px-1.5 text-[10px] font-bold tabular-nums"
                    style={{ color: col.color }}
                    aria-hidden
                  >
                    {col.items.length}
                  </span>
                </div>
                <div
                  className="flex-1 space-y-2 p-2 min-h-[120px]"
                  role="list"
                  aria-label={`${col.label} clients`}
                >
                  {col.items.length === 0 ? (
                    <p className="grid place-items-center py-10 text-center text-[11px] text-[#adb5bd]">
                      {isMobileActive ? "No clients in this stage yet." : "Drop here"}
                    </p>
                  ) : (
                    col.items.map((c) => (
                      <KanbanCard
                        key={c.id}
                        client={c}
                        cityName={c.city_id ? cityNameById.get(c.city_id) : undefined}
                        isDragging={dragId === c.id}
                        currentStage={col.key}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onQuickView={handleOpenQuickView}
                        onOpen={onOpen}
                        onMoveStage={handleMoveStage}
                        canEdit={clientCanEdit(c, me, userId)}
                        canDelete={!!canDelete}
                        onDelete={onDelete}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>


      <QuickViewDrawer
        client={quickViewClient}
        currentStage={quickViewClient ? stageOf(quickViewClient) : "prospekti"}
        onClose={() => setQuickViewId(null)}
        onOpenFull={onOpen}
        onStageChange={async (next) => {
          if (!quickViewClient) return;
          const id = quickViewClient.id;
          setPendingStage((p) => ({ ...p, [id]: next }));
          const res = await setStage({ data: { id, stage: next } });
          if (!res?.ok) {
            toast.error(res?.error ?? "Failed to update stage");
            setPendingStage((p) => {
              const { [id]: _drop, ...rest } = p;
              return rest;
            });
            return;
          }
          toast.success(`Moved to ${PIPELINE_STAGES.find((s) => s.key === next)?.label}`);
          onStageChange?.();
        }}
        onNotesSaved={() => onStageChange?.()}
      />
    </div>
  );
}

type KanbanCardProps = {
  client: any;
  cityName?: string;
  isDragging: boolean;
  currentStage: typeof PIPELINE_STAGES[number]["key"];
  canEdit: boolean;
  canDelete?: boolean;
  onDelete?: (id: string, name: string) => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onQuickView: (id: string) => void;
  onOpen: (id: string) => void;
  onMoveStage: (id: string, next: typeof PIPELINE_STAGES[number]["key"]) => void;
};

const KanbanCard = memo(function KanbanCard({
  client: c,
  cityName,
  isDragging,
  currentStage,
  canEdit,
  canDelete,
  onDelete,
  onDragStart,
  onDragEnd,
  onQuickView,
  onOpen,
  onMoveStage,
}: KanbanCardProps) {
  const seg = useMemo(() => SEGMENTS.find((s) => s.value === c.segment), [c.segment]);
  const segColor =
    c.segment === "eat_drink"
      ? "#f97316"
      : c.segment === "happenings"
      ? "#a855f7"
      : c.segment === "do"
      ? "#0ea5e9"
      : "#0d6efd";
  const displayName = c.company_name?.trim() || "—";
  const initials = useMemo(
    () =>
      String(displayName || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s: string) => s[0]?.toUpperCase())
        .join("") || "?",
    [displayName],
  );

  const labelId = useId();

  const open = useCallback(() => {
    onOpen(c.id);
  }, [onOpen, c.id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter") {
        e.preventDefault();
        open();
      } else if (e.key === " ") {
        e.preventDefault();
        onQuickView(c.id);
      }
    },
    [open, onQuickView, c.id],
  );

  return (
    <div
      role="listitem"
      tabIndex={0}
      aria-labelledby={labelId}
      aria-grabbed={isDragging}
      aria-readonly={!canEdit || undefined}
      draggable={canEdit}
      onDragStart={(e) => {
        if (!canEdit) {
          e.preventDefault();
          return;
        }
        onDragStart(c.id);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", c.id);
      }}
      onDragEnd={onDragEnd}
      onClick={open}
      onKeyDown={handleKeyDown}
      title={canEdit ? undefined : "Read-only: you don't have edit access for this client"}
      className={`group relative block overflow-hidden rounded-2xl border border-[#e7eaf0] bg-white shadow-[0_2px_10px_-2px_rgba(16,24,40,0.05)] transition-all hover:-translate-y-0.5 hover:border-[#0d6efd]/40 hover:shadow-[0_10px_28px_-12px_rgba(13,110,253,0.28)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2 ${
        canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-not-allowed"
      } ${isDragging ? "opacity-50 rotate-[0.5deg]" : ""}`}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ background: segColor }}
      />

      {canDelete && onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(c.id, displayName);
          }}
          aria-label={`Delete ${displayName}`}
          title="Delete client"
          className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-lg bg-white/80 text-[#adb5bd] backdrop-blur hover:bg-rose-50 hover:text-rose-600 transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}

      {/* Header */}
      <div className="flex items-start gap-2.5 p-3 pb-2.5 pr-10">
        <div
          aria-hidden
          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-[12px] font-bold text-white shadow-sm ring-4"
          style={{
            background: `linear-gradient(135deg, ${segColor}, ${segColor}cc)`,
            boxShadow: `0 0 0 4px ${segColor}1a`,
          }}
        >
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <h4
            id={labelId}
            className="text-[13px] font-bold leading-tight text-[#1a2540] break-words"
          >
            {displayName}
          </h4>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
            {seg && (
              <span
                className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                style={{ background: `${segColor}14`, color: segColor }}
              >
                {seg.label}
              </span>
            )}
            {!canEdit && (
              <span
                title="You don't have edit access for this client. Ask a super admin to grant access."
                className="inline-flex items-center gap-1 rounded-full border border-[#e7eaf0] bg-[#f4f6fa] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#6c757d]"
              >
                <LockIcon className="h-2.5 w-2.5" aria-hidden /> Read-only
              </span>
            )}
            {(cityName || c.business_id) && (
              <span className="truncate text-[11px] font-medium text-[#6c757d]">
                {cityName || c.business_id}
              </span>
            )}
          </div>
        </div>
      </div>




      {/* Contact info */}
      {(c.email || c.phone || cityName) && (
        <div className="space-y-1.5 border-y border-[#e7eaf0] bg-[#f8f9fb] px-3 py-2.5">
          {c.email && (
            <div className="flex items-center gap-2">
              <div className="grid h-6 w-6 shrink-0 place-items-center rounded-md border border-[#e7eaf0] bg-white">
                <Mail className="h-3 w-3 text-[#6c757d]" aria-hidden />
              </div>
              <span className="truncate text-[11px] font-medium text-[#1a2540]">{c.email}</span>
            </div>
          )}
          {c.phone && (
            <div className="flex items-center gap-2">
              <div className="grid h-6 w-6 shrink-0 place-items-center rounded-md border border-[#e7eaf0] bg-white">
                <Phone className="h-3 w-3 text-[#6c757d]" aria-hidden />
              </div>
              <span className="truncate text-[11px] font-medium text-[#1a2540]">{c.phone}</span>
            </div>
          )}
          {cityName && (
            <div className="flex items-center gap-2">
              <div className="grid h-6 w-6 shrink-0 place-items-center rounded-md border border-[#e7eaf0] bg-white">
                <MapPin className="h-3 w-3 text-[#6c757d]" aria-hidden />
              </div>
              <span className="truncate text-[11px] font-medium text-[#1a2540]">{cityName}</span>
            </div>
          )}
        </div>
      )}

      {/* Last contact attribution */}
      {c.last_contact && (
        <div className="flex items-center gap-2 border-b border-[#e7eaf0] bg-[#eff6ff] px-3 py-1.5">
          <div className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#0d6efd] text-[9px] font-bold uppercase text-white">
            {String(c.last_contact.by_name || "?").trim().charAt(0).toUpperCase() || "?"}
          </div>
          <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-[#0d6efd]">
            Contacted by {c.last_contact.by_name || "Unknown"}
            {c.last_contact.channel ? ` · ${String(c.last_contact.channel).replace(/_/g, " ")}` : ""}
          </span>
          <span className="ml-auto text-[10px] font-medium text-[#6c757d]">
            {new Date(c.last_contact.at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
          </span>
        </div>
      )}

      <div
        className="md:hidden flex items-center gap-1.5 border-b border-[#e7eaf0] bg-white px-3 py-2"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-[9px] font-bold uppercase tracking-wider text-[#6c757d]">
          Move
        </span>
        <select
          aria-label={`Move ${displayName} to another stage`}
          value={currentStage}
          disabled={!canEdit}
          title={canEdit ? undefined : "Read-only: you don't have edit access for this client"}
          onChange={(e) => {
            const next = e.target.value as typeof PIPELINE_STAGES[number]["key"];
            if (next !== currentStage) onMoveStage(c.id, next);
          }}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 truncate rounded-md border border-[#d8dde6] bg-white px-2 py-1 text-[11px] font-semibold text-[#1a2540] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {PIPELINE_STAGES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Footer: actions */}
      <div
        className="flex items-center justify-end gap-1.5 px-3 py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <MailComposeButton
          clientId={c.id}
          clientName={displayName}
          email={c.email}
        />
        <PhoneActionMenu phone={c.phone} displayName={displayName} />
        <ScheduleMeetingButton clientId={c.id} clientName={displayName} />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onQuickView(c.id);
          }}
          aria-label={`Quick view ${displayName}`}
          title="Quick view"
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-[#0d6efd] to-[#0b5ed7] text-white shadow-md shadow-[#0d6efd]/25 transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
        >
          <Eye className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );
});

function QuickViewDrawer({
  client,
  currentStage,
  onClose,
  onStageChange,
  onNotesSaved,
  onOpenFull,
}: {
  client: any | null;
  currentStage: typeof PIPELINE_STAGES[number]["key"];
  onClose: () => void;
  onStageChange: (next: typeof PIPELINE_STAGES[number]["key"]) => Promise<void> | void;
  onNotesSaved?: () => void;
  onOpenFull?: (id: string) => void;
}) {
  const updateNotes = useServerFn(crmUpdateClientNotes);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingStage, setSavingStage] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  useEffect(() => {
    setNotes(client?.notes ?? "");
  }, [client?.id, client?.notes]);

  const open = !!client;
  const stage = PIPELINE_STAGES.find((s) => s.key === currentStage) ?? PIPELINE_STAGES[0];
  const displayName = client?.company_name?.trim() || "—";

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        {client && (
          <>
            <SheetHeader>
              <SheetTitle className="truncate">{displayName}</SheetTitle>
              <SheetDescription>
                {client.business_id ? `Business ID ${client.business_id}` : "Quick view"}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-4">
              {(() => {
                const website = client.website
                  ? (client.website.startsWith("http") ? client.website : `https://${client.website}`)
                  : null;
                const waNumber = client.phone ? client.phone.replace(/[^\d]/g, "") : "";
                const actions: Array<{
                  key: string;
                  href: string;
                  label: string;
                  title: string;
                  icon: typeof Mail;
                  tone: string;
                  external?: boolean;
                }> = [];
                if (client.email) actions.push({ key: "email", href: `mailto:${client.email}`, label: "Email", title: client.email, icon: Mail, tone: "#0d6efd" });
                if (client.phone) actions.push({ key: "call", href: `tel:${client.phone}`, label: "Call", title: client.phone, icon: Phone, tone: "#10b981" });
                if (waNumber) actions.push({ key: "wa", href: `https://wa.me/${waNumber}`, label: "WhatsApp", title: `WhatsApp ${client.phone}`, icon: WhatsAppIcon as any, tone: "#25D366", external: true });
                if (website) actions.push({ key: "web", href: website, label: "Website", title: client.website, icon: Globe, tone: "#6f42c1", external: true });

                if (actions.length === 0) {
                  return (
                    <div className="rounded-lg border border-dashed border-[#e7eaf0] bg-[#f8f9fb] px-3 py-2.5 text-xs text-[#6c757d]">
                      No contact info on file.
                    </div>
                  );
                }
                return (
                  <div className="flex flex-wrap gap-2">
                    {actions.map((a) => {
                      const Icon = a.icon;
                      if (a.key === "email") {
                        return (
                          <button
                            key={a.key}
                            type="button"
                            onClick={() => setComposeOpen(true)}
                            title={`Compose email — ${a.title}`}
                            aria-label={`Compose email to ${a.title}`}
                            className="group inline-flex h-10 w-10 items-center justify-center rounded-full border bg-white text-[#475467] shadow-sm transition-all hover:-translate-y-0.5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                            style={{ borderColor: `${a.tone}33` }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = a.tone; (e.currentTarget as HTMLButtonElement).style.borderColor = a.tone; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = ""; (e.currentTarget as HTMLButtonElement).style.borderColor = `${a.tone}33`; }}
                          >
                            <Icon className="h-4 w-4" aria-hidden />
                          </button>
                        );
                      }
                      return (
                        <a
                          key={a.key}
                          href={a.href}
                          title={a.title}
                          aria-label={`${a.label}: ${a.title}`}
                          {...(a.external ? { target: "_blank", rel: "noreferrer" } : {})}
                          className="group inline-flex h-10 w-10 items-center justify-center rounded-full border bg-white text-[#475467] shadow-sm transition-all hover:-translate-y-0.5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                          style={{ borderColor: `${a.tone}33` }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = a.tone; (e.currentTarget as HTMLAnchorElement).style.borderColor = a.tone; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = ""; (e.currentTarget as HTMLAnchorElement).style.borderColor = `${a.tone}33`; }}
                        >
                          <Icon className="h-4 w-4" aria-hidden />
                        </a>
                      );
                    })}
                  </div>
                );
              })()}


              <div>
                <label
                  htmlFor="qv-stage"
                  className="block text-[11px] font-semibold uppercase tracking-wider text-[#475467] mb-1.5"
                >
                  Pipeline Stage
                </label>
                <select
                  id="qv-stage"
                  value={currentStage}
                  disabled={savingStage}
                  onChange={async (e) => {
                    const next = e.target.value as typeof PIPELINE_STAGES[number]["key"];
                    setSavingStage(true);
                    await onStageChange(next);
                    setSavingStage(false);
                  }}
                  className="w-full rounded-md border px-3 py-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd]"
                  style={{ background: stage.bg, color: stage.color, borderColor: `${stage.color}55` }}
                >
                  {PIPELINE_STAGES.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="qv-notes"
                  className="block text-[11px] font-semibold uppercase tracking-wider text-[#475467] mb-1.5"
                >
                  Notes
                </label>
                <Textarea
                  id="qv-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={6}
                  placeholder="Add internal notes about this client…"
                  className="text-sm"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNotes(client.notes ?? "")}
                    disabled={savingNotes || (notes ?? "") === (client.notes ?? "")}
                  >
                    Reset
                  </Button>
                  <Button
                    size="sm"
                    disabled={savingNotes || (notes ?? "") === (client.notes ?? "")}
                    onClick={async () => {
                      setSavingNotes(true);
                      const res = await updateNotes({
                        data: { id: client.id, notes: notes.trim() ? notes : null },
                      });
                      setSavingNotes(false);
                      if (!res?.ok) {
                        toast.error(res?.error ?? "Failed to save notes");
                        return;
                      }
                      toast.success("Notes saved");
                      onNotesSaved?.();
                    }}
                  >
                    {savingNotes && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                    Save Notes
                  </Button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenFull?.(client.id);
                }}
                className="block w-full rounded-md border border-[#e7eaf0] bg-white px-3 py-2 text-center text-xs font-semibold text-[#0d6efd] hover:bg-[#f4f6fa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd]"
              >
                Open Full Profile →
              </button>
            </div>
          </>
        )}
      </SheetContent>
      {client?.email && (
        <Suspense fallback={null}>
          <ComposeEmailDialog
            open={composeOpen}
            onClose={() => setComposeOpen(false)}
            clientId={client.id}
            clientName={displayName}
            toEmail={client.email}
          />
        </Suspense>
      )}
    </Sheet>
  );
}


const CardActionIcon = memo(function CardActionIcon({
  href,
  label,
  tint,
  Icon,
}: {
  href: string | null;
  label: string;
  tint: string;
  Icon: React.ComponentType<{ className?: string }>;
}) {
  const disabled = !href;
  const className =
    "group/icn inline-flex h-7 w-7 items-center justify-center rounded-lg border border-[#e7eaf0] bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2";


  if (disabled) {
    return (
      <span
        role="img"
        aria-label={label}
        title={label}
        className={`${className} cursor-not-allowed opacity-40`}
        style={{ color: tint, borderColor: "#e7eaf0", background: "#fff" }}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
    );
  }
  return (
    <a
      href={href}
      aria-label={label}
      title={label}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={className}
      style={{ color: tint, borderColor: `${tint}33`, background: `${tint}0d` }}
    >
      <Icon className="h-3.5 w-3.5 transition-transform group-hover/icn:scale-110" aria-hidden />
    </a>
  );

});

function MailComposeButton({ clientId, clientName, email }: { clientId: string; clientName: string; email: string | null | undefined }) {
  const [open, setOpen] = useState(false);
  const tint = "#0d6efd";
  const className =
    "group/icn inline-flex h-7 w-7 items-center justify-center rounded-lg border shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2";
  if (!email) {
    return (
      <span
        role="img"
        aria-label="No email on file"
        title="No email on file"
        className={`${className} cursor-not-allowed opacity-40`}
        style={{ color: tint, borderColor: "#e7eaf0", background: "#fff" }}
      >
        <Mail className="h-3.5 w-3.5" />
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={`Compose email to ${clientName}`}
        title={`Compose email — ${email}`}
        className={className}
        style={{ color: tint, borderColor: `${tint}33`, background: `${tint}0d` }}
      >
        <Mail className="h-3.5 w-3.5 transition-transform group-hover/icn:scale-110" aria-hidden />
      </button>
      {open && (
        <div onClick={(e) => e.stopPropagation()}>
          <Suspense fallback={null}>
            <ComposeEmailDialog
              open={open}
              onClose={() => setOpen(false)}
              clientId={clientId}
              clientName={clientName}
              toEmail={email}
            />
          </Suspense>
        </div>
      )}
    </>
  );
}

function PhoneActionMenu({ phone, displayName }: { phone: string | null | undefined; displayName: string }) {
  const tint = "#10b981";
  const className =
    "group/icn inline-flex h-7 w-7 items-center justify-center rounded-lg border shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2";

  if (!phone) {
    return (
      <span
        role="img"
        aria-label="No phone on file"
        title="No phone on file"
        className={`${className} cursor-not-allowed opacity-40`}
        style={{ color: tint, borderColor: "#e7eaf0", background: "#fff" }}
      >
        <Phone className="h-3.5 w-3.5" />
      </span>
    );
  }

  const waNumber = phone.replace(/[^\d+]/g, "").replace(/^\+/, "");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Contact ${displayName}`}
          title={`Contact ${displayName}`}
          onClick={(e) => e.stopPropagation()}
          className={className}
          style={{ color: tint, borderColor: `${tint}33`, background: `${tint}0d` }}
        >
          <Phone className="h-3.5 w-3.5 transition-transform group-hover/icn:scale-110" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
        className="w-44"
      >
        <DropdownMenuItem asChild>
          <a href={`tel:${phone}`} className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-[#10b981]" /> Phone Call
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href={`https://wa.me/${waNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <WhatsAppIcon className="h-4 w-4" /> WhatsApp Message
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      aria-hidden
      fill="#25D366"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.802 2.708.802.66 0 2.092-.516 2.378-1.117.085-.215.085-.43.085-.658 0-.272 0-.516-.157-.717-.214-.272-1.91-.954-2.278-1.117l-.001-.001zM16.205 0C7.27 0 .002 7.27.002 16.205c0 2.85.745 5.643 2.16 8.06l-1.49 5.514a.6.6 0 0 0 .742.74l5.706-1.488a16.144 16.144 0 0 0 7.882 2.04C25.137 30.41 32.41 23.14 32.41 14.205S25.14.002 16.205.002zm0 28.85c-2.435 0-4.83-.66-6.927-1.91a.617.617 0 0 0-.46-.071l-4.388 1.145 1.16-4.272a.602.602 0 0 0-.085-.515 12.62 12.62 0 0 1-2.11-7.024c0-7.034 5.72-12.755 12.81-12.755 7.024 0 12.74 5.72 12.74 12.755-.014 7.025-5.73 12.747-12.74 12.747z" />
    </svg>
  );
}

function ScheduleMeetingButton({
  clientId,
  clientName,
}: {
  clientId: string;
  clientName: string;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState("10:00");
  const [meetingId, setMeetingId] = useState("");
  const [kind, setKind] = useState<"video" | "call" | "in_person">("video");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const tint = "#a855f7";
  const schedule = useServerFn(crmScheduleMeeting);
  const gStart = useServerFn(googleCalendarStart);
  const gStatusFn = useServerFn(googleCalendarStatus);
  const gCreateMeet = useServerFn(createGoogleMeetForMeeting);
  const gStatus = useQuery({
    queryKey: ["google-calendar-status"],
    queryFn: () => gStatusFn({}),
    staleTime: 60_000,
    enabled: open,
  });
  const [creatingMeet, setCreatingMeet] = useState(false);

  const reset = () => {
    setDate(undefined);
    setTime("10:00");
    setMeetingId("");
    setKind("video");
    setNotes("");
  };

  const connectGoogle = async () => {
    try {
      const { url } = await gStart({});
      const w = window.open(url, "google-oauth", "width=520,height=640");
      const onMsg = (e: MessageEvent) => {
        if (e.data?.type === "google-calendar-connected") {
          window.removeEventListener("message", onMsg);
          gStatus.refetch();
          toast.success("Google Calendar connected");
        } else if (e.data?.type === "google-calendar-error") {
          window.removeEventListener("message", onMsg);
          const title = e.data?.title || "Google connection failed";
          const msg = e.data?.message || "";
          toast.error(title, { description: msg, duration: 8000 });
        }
      };
      window.addEventListener("message", onMsg);
      if (!w) toast.error("Popup blocked — allow popups and try again");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not start Google connect");
    }
  };

  const createMeetNow = async () => {
    if (!date) {
      toast.error("Pick date & time first");
      return;
    }
    const [hh, mm] = time.split(":").map((n) => parseInt(n, 10));
    const when = new Date(date);
    when.setHours(hh || 0, mm || 0, 0, 0);
    if (when.getTime() < Date.now()) {
      toast.error("Meeting time must be in the future");
      return;
    }
    setCreatingMeet(true);
    try {
      const r = await gCreateMeet({
        data: {
          client_id: clientId,
          scheduled_at: when.toISOString(),
          duration_minutes: 30,
          notes: notes || null,
        },
      });
      if (r.hangoutLink) {
        setMeetingId(r.hangoutLink);
        toast.success("Google Meet created & invites sent");
      } else {
        toast.success("Google Calendar event created");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Could not create Meet");
    } finally {
      setCreatingMeet(false);
    }
  };

  const genId = () => {
    const id = `mtg-${Math.random().toString(36).slice(2, 6)}-${Math.random()
      .toString(36)
      .slice(2, 6)}`;
    setMeetingId(id);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) {
      toast.error("Pick a date");
      return;
    }
    const [hh, mm] = time.split(":").map((n) => parseInt(n, 10));
    const when = new Date(date);
    when.setHours(hh || 0, mm || 0, 0, 0);
    if (when.getTime() < Date.now()) {
      toast.error("Meeting time must be in the future");
      return;
    }
    setBusy(true);
    try {
      const r = await schedule({
        data: {
          client_id: clientId,
          scheduled_at: when.toISOString(),
          kind,
          meeting_id: meetingId || null,
          notes: notes || null,
        },
      });
      const parts: string[] = [];
      if (r.sent?.client) parts.push("client");
      if (r.sent?.rep) parts.push("you");
      const who = parts.length ? ` — emailed ${parts.join(" & ")}` : "";
      toast.success(`Meeting scheduled with ${clientName}${who}`);
      setOpen(false);
      reset();
    } catch (err: any) {
      toast.error(err?.message ?? "Could not schedule meeting");
    } finally {
      setBusy(false);
    }
  };

  const kindOptions: { key: typeof kind; label: string; icon: any }[] = [
    { key: "video", label: "Video", icon: Video },
    { key: "call", label: "Phone", icon: Phone },
    { key: "in_person", label: "In Person", icon: Users },
  ];

  return (
    <>
      <button
        type="button"
        aria-label={`Schedule meeting with ${clientName}`}
        title="Schedule meeting"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="group/icn inline-flex h-7 w-7 items-center justify-center rounded-lg border shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-2"
        style={{ color: tint, borderColor: `${tint}33`, background: `${tint}0d` }}
      >
        <CalendarPlus className="h-3.5 w-3.5 transition-transform group-hover/icn:scale-110" aria-hidden />
      </button>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) reset();
        }}
      >
        <DialogContent
          className="sm:max-w-[440px] p-0 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-br from-[#0d1b3d] to-[#0d6efd] px-5 py-4 text-white">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 backdrop-blur">
                <CalendarPlus className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base font-semibold text-white">Schedule Meeting</DialogTitle>
                <DialogDescription className="text-xs text-white/80 truncate">
                  With <span className="font-medium text-white">{clientName}</span>
                </DialogDescription>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4 px-5 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Type</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {kindOptions.map((o) => {
                  const Icon = o.icon;
                  const active = kind === o.key;
                  return (
                    <button
                      type="button"
                      key={o.key}
                      onClick={() => setKind(o.key)}
                      className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-medium transition ${
                        active
                          ? "border-[#0d6efd] bg-[#0d6efd]/10 text-[#0d1b3d]"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {kind === "video" && (
              <div className="space-y-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`mtg-id-${clientId}`} className="text-xs font-medium text-slate-600">
                    Meeting Link / ID <span className="text-slate-400 font-normal">(optional)</span>
                  </Label>
                  <Input
                    id={`mtg-id-${clientId}`}
                    value={meetingId}
                    onChange={(e) => setMeetingId(e.target.value)}
                    placeholder="https://meet.google.com/… or paste link"
                    className="h-9"
                  />
                </div>
                {gStatus.data?.connected ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2">
                    <div className="min-w-0 text-[11px] text-emerald-800">
                      <span className="font-medium">Google Calendar connected</span>
                      {gStatus.data.email && (
                        <div className="truncate text-emerald-700/80">{gStatus.data.email}</div>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={createMeetNow}
                      disabled={creatingMeet || !date}
                      className="h-8 bg-[#0d6efd] hover:bg-[#0b5ed7] text-white"
                    >
                      {creatingMeet ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <Video className="mr-1 h-3.5 w-3.5" />
                          Create Meet & Invite
                        </>
                      )}
                    </Button>
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={genId}
                    className="text-[11px] text-slate-500 hover:text-slate-700 underline"
                  >
                    Or generate a placeholder ID
                  </button>
                </div>
              </div>
            )}

            {kind === "call" && (
              <div className="space-y-1.5">
                <Label htmlFor={`mtg-id-${clientId}`} className="text-xs font-medium text-slate-600">
                  Phone Number <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id={`mtg-id-${clientId}`}
                  value={meetingId}
                  onChange={(e) => setMeetingId(e.target.value)}
                  placeholder="e.g. +358 40 123 4567"
                  className="h-9"
                />
              </div>
            )}

            {kind === "in_person" && (
              <div className="space-y-1.5">
                <Label htmlFor={`mtg-id-${clientId}`} className="text-xs font-medium text-slate-600">
                  Location <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id={`mtg-id-${clientId}`}
                  value={meetingId}
                  onChange={(e) => setMeetingId(e.target.value)}
                  placeholder="e.g. Office, café address…"
                  className="h-9"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-9 w-full justify-start font-normal"
                    >
                      <CalendarPlus className="mr-2 h-3.5 w-3.5" />
                      {date ? format(date, "PP") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`mtg-time-${clientId}`} className="text-xs font-medium text-slate-600">
                  Time
                </Label>
                <Input
                  id={`mtg-time-${clientId}`}
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`mtg-notes-${clientId}`} className="text-xs font-medium text-slate-600">
                Notes <span className="text-slate-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                id={`mtg-notes-${clientId}`}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Agenda, link, etc."
                className="resize-none"
              />
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-700">Auto reminders:</span> confirmation now, then <strong>4h</strong>, <strong>1h</strong> and <strong>10 min</strong> before — sent to the client and you.
            </div>

            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#0d1b3d] hover:bg-[#0d1b3d]/90" disabled={busy}>
                {busy ? "Scheduling…" : "Schedule"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}




function StageSelect({
  clientId,
  value,
  canEdit = true,
  onChanged,
}: {
  clientId: string;
  value: typeof PIPELINE_STAGES[number]["key"];
  canEdit?: boolean;
  onChanged?: () => void;
}) {
  const setStage = useServerFn(crmSetClientStage);
  const [busy, setBusy] = useState(false);
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const current = optimistic ?? value;
  const stage = PIPELINE_STAGES.find((s) => s.key === current) ?? PIPELINE_STAGES[0];
  return (
    <span className="inline-flex items-center gap-1">
      {!canEdit && (
        <span
          title="Read-only: you don't have edit access for this client"
          className="inline-flex items-center gap-1 rounded-full border border-[#e7eaf0] bg-[#f4f6fa] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#6c757d]"
        >
          <LockIcon className="h-2.5 w-2.5" aria-hidden /> Read-only
        </span>
      )}
      <select
        value={current}
        disabled={busy || !canEdit}
        title={canEdit ? undefined : "Read-only: you don't have edit access for this client"}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onChange={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const next = e.target.value as typeof PIPELINE_STAGES[number]["key"];
          if (next === current) return;
          setBusy(true);
          setOptimistic(next);
          const res = await setStage({ data: { id: clientId, stage: next } });
          setBusy(false);
          if (!res?.ok) {
            const msg = res?.error ?? "";
            const reason = /forbidden|don't have edit|permission|not allowed/i.test(msg)
              ? "You can't move this client. You only have edit access to clients assigned to you — ask a super admin to grant edit access for this rep's clients."
              : msg || "Failed to update stage.";
            toast.error("Pipeline move blocked", { description: reason });
            setOptimistic(null);
            return;
          }
          toast.success(`Moved to ${PIPELINE_STAGES.find((s) => s.key === next)?.label}`);
          onChanged?.();
        }}
        aria-label="Pipeline stage"
        className="inline-flex shrink-0 rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d6efd] focus-visible:ring-offset-1 appearance-none bg-no-repeat bg-[right_0.4rem_center] pr-6 disabled:cursor-not-allowed disabled:opacity-70"
        style={{ background: stage.bg, color: stage.color, borderColor: `${stage.color}55` }}
      >
        {PIPELINE_STAGES.map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>
    </span>
  );
}

function NewClientDialog({
  open,
  onClose,
  cities,
  onCreated,
  onCityAdded,
}: {
  open: boolean;
  onClose: () => void;
  cities: any[];
  onCreated: () => void;
  onCityAdded?: () => void;
}) {
  const upsert = useServerFn(crmUpsertClient);
  const createCity = useServerFn(crmCreateCity);
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [cityId, setCityId] = useState<string>("");
  const [segment, setSegment] = useState<SegmentVal>("eat_drink");
  const [categorySlug, setCategorySlug] = useState<string>("");
  const [subcategory, setSubcategory] = useState("");
  const [manualSub, setManualSub] = useState(false);

  const section = SEGMENT_TO_SECTION[segment];
  const categoriesQ = useSectionCategories(section);
  const categories = (categoriesQ.data ?? []).filter((c) => c.slug !== "all");
  const selectedCategory = categories.find((c) => c.slug === categorySlug);

  const placesQ = useQuery({
    queryKey: ["crm-new-client-places", section, categorySlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("places")
        .select("id,name")
        .eq("section", section)
        .eq("category_slug", categorySlug)
        .order("name")
        .limit(500);
      if (error) throw error;
      return (data ?? []) as { id: string; name: string }[];
    },
    enabled: !!categorySlug,
    staleTime: 60_000,
  });
  const places = placesQ.data ?? [];

  // Reset category/subcategory when segment changes
  useEffect(() => {
    setCategorySlug("");
    setSubcategory("");
    setManualSub(false);
  }, [segment]);
  useEffect(() => {
    setSubcategory("");
    setManualSub(false);
  }, [categorySlug]);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [createListing, setCreateListing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingCity, setAddingCity] = useState(false);
  const [newCityName, setNewCityName] = useState("");
  const [creatingCity, setCreatingCity] = useState(false);

  const submitNewCity = async () => {
    const n = newCityName.trim();
    if (n.length < 2) {
      toast.error("Enter at least 2 characters");
      return;
    }
    setCreatingCity(true);
    try {
      const res = await createCity({ data: { name: n } });
      toast.success(`Added ${res.city.name}`);
      setCityId(res.city.id);
      setNewCityName("");
      setAddingCity(false);
      onCityAdded?.();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to add city");
    } finally {
      setCreatingCity(false);
    }
  };

  const reset = () => {
    setName("");
    setCompanyName("");
    setBusinessId("");
    setCityId("");
    setSegment("eat_drink");
    setCategorySlug("");
    setSubcategory("");
    setManualSub(false);
    setPhone("");
    setEmail("");
    setNotes("");
    setCreateListing(false);
  };

  // Compose subcategory persisted on the client row: "Category · Subcategory"
  const composedSubcategory = (() => {
    const sub = subcategory.trim();
    const catLabel = selectedCategory?.label?.trim() ?? "";
    if (catLabel && sub) return `${catLabel} · ${sub}`;
    if (catLabel) return catLabel;
    return sub || "";
  })();

  const businessIdRe = /^\d{7}-\d$/;
  const bidTrim = businessId.trim();
  const bidError =
    bidTrim.length > 0 && !businessIdRe.test(bidTrim)
      ? "Format must be 1234567-8"
      : null;

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    const emailTrim = email.trim();
    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (bidError) {
      toast.error(bidError);
      return;
    }
    if (createListing && !cityId) {
      toast.error("City is required to create an admin listing");
      return;
    }
    if (createListing && !categorySlug) {
      toast.error("Category is required to create an admin listing");
      return;
    }
    setSaving(true);
    try {
      const res = await upsert({
        data: {
          name: name.trim(),
          company_name: companyName.trim() || null,
          business_id: bidTrim || null,
          city_id: cityId || null,
          segment,
          subcategory: composedSubcategory || null,
          phone: phone || null,
          email: emailTrim || null,
          notes: notes || null,
          status: "prospect",
          create_admin_listing: createListing,
          listing_category_slug: createListing ? categorySlug || null : null,
          listing_category_label: createListing ? selectedCategory?.label ?? null : null,
        },
      });

      if (res?.error) {
        toast.error(res.error);
        return;
      }
      if ((res as any)?.listing_error) {
        toast.warning(`Client created, but listing failed: ${(res as any).listing_error}`);
      } else if (createListing) {
        toast.success("Client created · Draft listing added to Admin Panel");
      } else {
        toast.success("Client created");
      }
      reset();
      onCreated();
      onClose();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Client</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium">Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium">Company Name</label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Official company name"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Business ID</label>
              <Input
                value={businessId}
                onChange={(e) => setBusinessId(e.target.value)}
                placeholder="e.g. 1234567-8"
                aria-invalid={!!bidError}
                className={bidError && bidTrim ? "border-destructive focus-visible:ring-destructive" : ""}
              />
              {bidError && bidTrim && (
                <p className="mt-1 text-[11px] text-destructive">{bidError}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="block text-xs font-medium">City</label>
                {!addingCity && (
                  <button
                    type="button"
                    onClick={() => setAddingCity(true)}
                    className="text-[11px] font-semibold text-[#0d6efd] hover:underline"
                  >
                    + Add new
                  </button>
                )}
              </div>
              {addingCity ? (
                <div className="flex gap-1">
                  <Input
                    autoFocus
                    value={newCityName}
                    onChange={(e) => setNewCityName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        submitNewCity();
                      }
                      if (e.key === "Escape") {
                        setAddingCity(false);
                        setNewCityName("");
                      }
                    }}
                    placeholder="New city name"
                    className="h-9"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={submitNewCity}
                    disabled={creatingCity}
                    className="h-9 shrink-0"
                  >
                    {creatingCity ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setAddingCity(false);
                      setNewCityName("");
                    }}
                    className="h-9 shrink-0 px-2"
                    aria-label="Cancel"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <Select value={cityId} onValueChange={setCityId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Segment *</label>
              <Select value={segment} onValueChange={(v) => setSegment(v as SegmentVal)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEGMENTS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">
              Category {categoriesQ.isLoading && <span className="text-[10px] text-[#6c757d]">(loading…)</span>}
            </label>
            <Select
              value={categorySlug || undefined}
              onValueChange={(v) => setCategorySlug(v)}
              disabled={categoriesQ.isLoading || categories.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={categories.length === 0 ? "No categories for this segment" : "Select a category"} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Subcategory</label>
            {categorySlug && !manualSub ? (
              <>
                <Select
                  value={subcategory || undefined}
                  onValueChange={(v) => setSubcategory(v)}
                  disabled={placesQ.isLoading}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        placesQ.isLoading
                          ? "Loading places…"
                          : places.length === 0
                            ? "No places in this category"
                            : "Pick a place from this category"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {places.map((p) => (
                      <SelectItem key={p.id} value={p.name}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => {
                    setManualSub(true);
                    setSubcategory("");
                  }}
                  className="mt-1 text-[11px] font-semibold text-[#0d6efd] hover:underline"
                >
                  Can't find it? Add manually
                </button>
              </>
            ) : (
              <>
                <Input
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  placeholder={categorySlug ? "Type a custom subcategory" : "e.g. Cafe, Sauna, Festival"}
                />
                {categorySlug && (
                  <button
                    type="button"
                    onClick={() => {
                      setManualSub(false);
                      setSubcategory("");
                    }}
                    className="mt-1 text-[11px] font-semibold text-[#0d6efd] hover:underline"
                  >
                    ← Pick from list
                  </button>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium">Phone</label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
              />
            </div>

          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          <label
            className="flex items-start gap-2 rounded-md border bg-[#f8f9fc] p-2.5 cursor-pointer select-none"
            style={{ borderColor: "#dee2e6" }}
          >
            <input
              type="checkbox"
              checked={createListing}
              onChange={(e) => setCreateListing(e.target.checked)}
              className="mt-0.5 size-4 shrink-0 accent-[#0d6efd]"
            />
            <span className="text-xs leading-snug">
              <span className="font-semibold text-[#212529]">
                Also add this customer to the Admin Panel
              </span>
              <span className="block text-[11px] text-[#6c757d] mt-0.5">
                Creates a hidden draft listing under the selected city & category. An admin can then
                edit it, add photos, and publish it to the website.
              </span>
              {createListing && (!cityId || !categorySlug) && (
                <span className="mt-1 block text-[11px] font-medium text-[#dc3545]">
                  City and category are required.
                </span>
              )}
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
